"use client";

import React from "react";
import { ArrowRight, Dna, Activity, Brain, Microscope } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PersonalizedMedicationManagement() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll();
  const features = [{
    icon: <Dna className="size-6 text-primary" />,
    title: "Genetic Evaluation",
    description: "We analyze your unique genetic markers to understand how your body metabolizes medications."
  }, {
    icon: <Brain className="size-6 text-primary" />,
    title: "Targeted Treatment",
    description: "Precise medication selection reduces the trial-and-error process common in psychiatry."
  }, {
    icon: <Microscope className="size-6 text-primary" />,
    title: "Evidence-Based",
    description: "Our approach combines advanced scientific data with clinical expertise for optimal outcomes."
  }];
  return <section id="personalized-medication-management" className="w-full min-h-screen bg-background overflow-hidden flex flex-col lg:flex-row">
      {/* Visual Side */}
      <div className="w-full lg:w-2/5 h-[50vh] lg:h-auto relative bg-gradient-to-br from-background to-secondary/30 bg-no-repeat bg-center bg-contain !bg-[url(https://vast-cheetah.10web.cloud/wp-content/uploads/2026/04/loop-dna-GIF-flip-rev-crop.gif)]">
      </div>

      {/* Content Side */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-6 md:p-12 lg:p-20 xl:p-24 bg-background">
        <div ref={textRef} className={`max-w-xl mx-auto lg:mx-0 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/50 text-accent-foreground text-xs font-medium mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
            </span>
            Advanced Genetic Insights
          </div>

          <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-6 leading-tight">Personalized Medication Management</h2>
          
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            At Sharmat Psychiatric Group, we move beyond the standard "one-size-fits-all" approach. 
            By integrating cutting-edge genetic evaluation, we tailor medication plans to your specific biological profile, 
            minimizing side effects and maximizing efficacy.
          </p>

          <div className="grid gap-6 mb-10">
            {features.map((feature, i) => <Card key={i} className="border-none shadow-none bg-transparent p-0" data-index={i}>
                <CardContent className="p-0 flex gap-4">
                  <div className="shrink-0 mt-1 p-3 rounded-lg bg-secondary/50 h-fit">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
              <Link to="/ContactPage">
                Book a Consultation
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary/50">
              <Link to="/PrecisionCarePage">
                Learn About Our Process
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}